import { AnalyticsBrowser } from '@segment/analytics-next';
import { isSyntheticMonitor } from './utils';

interface TrackPageViewedParams {
  name: string;
  title: string;
  keywords: string[];
  referrer?: string;
  url?: string;
  path?: string;
}

interface TrackFormStartedParams {
  form_name: string;
  feature_name?: string;
  source_email_name?: string;
  page?: string;
  path?: string;
}

interface TrackStepCompletedParams {
  step: number;
  step_label: string;
  step_name: string;
  answer_labels?: string[];
  answer_values?: string[];
  form_name: string;
  path?: string;
}

interface TrackStepStartedParams {
  step: number;
  step_label: string;
  step_name: string;
  form_name: string;
  path?: string;
}

interface TrackStepErrorViewedParams {
  step: number;
  step_label: string;
  step_name: string;
  error_type: 'validation' | 'system';
  error_message: string;
  error_field?: string;
  form_name: string;
  path?: string;
}

interface TrackFormSubmittedParams {
  fan: {
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string;
    postal_code: string;
  };
  trusted_referral_key: string;
  page_name?: string;
  form_name?: string;
  path?: string;
}

interface TrackElementClickedParams {
  element: string;
  label: string;
  page: string;
  referral_key: string;
  feature_name?: string;
  path?: string;
}

interface TrackProfileAttributeViewedParams {
  attribute_label: string;
  attributes_displayed: string[];
  referral_key: string;
  pro_account_key?: string;
  pro_office_key?: string;
  path?: string;
}

interface TrackMatchResultReturnedParams {
  result_count: number;
  search_terms: string[];
  form_name: string;
  pro_office_key?: string | null;
  match_score?: number | null;
  distance_score?: number | null;
  category_score?: number | null;
  distance_miles?: number | null;
  runner_up_pro_office_key?: string | null;
  runner_up_match_score?: number | null;
  match_result_key?: string | null;
  rank?: number | null;
  skipped_full_count?: number | null;
  tie_break_applied?: boolean | null;
  tied_account_count?: number | null;
  meeting_score?: number | null;
  runner_up_account_external_key?: string | null;
  raw_result_count?: number | null;
  search_depth?: number | null;
  path?: string;
}

interface TrackVirtualMeetingSchedulingButtonClickedParams {
  partnership_urn: string;
  company_name: string;
  page: string;
  referral_key: string;
  feature_name?: string;
  path?: string;
}

interface TrackExperimentViewedParams {
  experiment_id: string;
  variation_id: string;
  experiment_name: string;
  variation_name: string;
  experimentation_platform: string;
}

interface IdentifyExperimentParams {
  userId: string;
  experiment_key: string;
  variation_key: string;
}

const analytics_event_names = {
  PAGE_VIEWED: 'Page Viewed',
  FORM_STARTED: 'Form Started',
  STEP_STARTED: 'Step Started',
  STEP_COMPLETED: 'Step Completed',
  STEP_ERROR_VIEWED: 'Step Error Viewed',
  FORM_SUBMITTED: 'Form Submitted',
  ELEMENT_CLICKED: 'Element Clicked',
  EXPERIMENT_VIEWED: 'Experiment Viewed',
  PROFILE_ATTRIBUTE_VIEWED: 'Profile Attribute Viewed',
  MATCH_RESULT_RETURNED: 'Match Result Returned'
};

function trackPageViewed({
  name,
  title,
  keywords,
  referrer,
  url = getFullUrl(),
  path = getLocationPath()
}: TrackPageViewedParams): void {
  getAnalytics()?.track(analytics_event_names.PAGE_VIEWED, {
    keywords,
    name,
    path,
    referrer,
    title,
    url,
    utm_params: getUtmParams()
  });
}

function trackFormStarted({
  form_name,
  feature_name,
  source_email_name,
  page,
  path = getLocationPath()
}: TrackFormStartedParams): void {
  getAnalytics()?.track(analytics_event_names.FORM_STARTED, {
    feature_name,
    form_name,
    page,
    path,
    source_email_name,
    form_type: 'lead',
    program: 'tax'
  });
}

function trackStepCompleted({
  step,
  step_label,
  step_name,
  answer_labels,
  answer_values,
  form_name,
  path = getLocationPath()
}: TrackStepCompletedParams): void {
  getAnalytics()?.track(analytics_event_names.STEP_COMPLETED, {
    answer_labels,
    answer_values,
    form_name,
    path,
    step,
    step_label,
    step_name,
    program: 'tax'
  });
}

function trackStepStarted({
  step,
  step_label,
  step_name,
  form_name,
  path = getLocationPath()
}: TrackStepStartedParams): void {
  getAnalytics()?.track(analytics_event_names.STEP_STARTED, {
    form_name,
    path,
    step,
    step_label,
    step_name,
    program: 'tax'
  });
}

function trackStepErrorViewed({
  step,
  step_label,
  step_name,
  error_type,
  error_message,
  error_field,
  form_name,
  path = getLocationPath()
}: TrackStepErrorViewedParams): void {
  getAnalytics()?.track(analytics_event_names.STEP_ERROR_VIEWED, {
    error_field,
    error_message,
    error_type,
    form_name,
    path,
    step,
    step_label,
    step_name,
    program: 'tax'
  });
}

async function trackFormSubmitted({
  fan,
  trusted_referral_key,
  form_name,
  page_name = getPageTitle(),
  path = getLocationPath()
}: TrackFormSubmittedParams): Promise<void> {
  const analytics = getAnalytics();
  if (!analytics) return;

  // Before analytics.js loads, the snippet stub only queues track() and returns a
  // non-thenable, so callers waiting on delivery would redirect immediately.
  await new Promise<void>((resolve) => {
    analytics.ready(() => resolve());
  });
  await analytics.track(analytics_event_names.FORM_SUBMITTED, {
    ...fan,
    form_name,
    page_name,
    path,
    trusted_referral_key,
    form_type: 'Lead',
    trusted_program: 'tax'
  });
}

function trackElementClicked({
  element,
  label,
  page,
  referral_key,
  feature_name,
  path = getLocationPath()
}: TrackElementClickedParams): Promise<unknown> | undefined {
  return getAnalytics()?.track(analytics_event_names.ELEMENT_CLICKED, {
    element,
    feature_name,
    label,
    page,
    path,
    referral_key,
    program: 'tax'
  });
}

function trackProfileAttributeViewed({
  attribute_label,
  attributes_displayed,
  pro_account_key,
  referral_key,
  pro_office_key,
  path = getLocationPath()
}: TrackProfileAttributeViewedParams): void {
  getAnalytics()?.track(analytics_event_names.PROFILE_ATTRIBUTE_VIEWED, {
    attribute_label,
    attributes_displayed,
    pro_account_key,
    referral_key,
    pro_office_key,
    path
  });
}

function trackMatchResultReturned({
  path = getLocationPath(),
  ...properties
}: TrackMatchResultReturnedParams): void {
  // Tracking plan types these as non-nullable; omit missing values instead of sending null.
  const presentProperties = Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value != null)
  );
  getAnalytics()?.track(analytics_event_names.MATCH_RESULT_RETURNED, {
    ...presentProperties,
    path,
    program: 'tax'
  });
}

function trackVirtualMeetingSchedulingButtonClicked({
  partnership_urn,
  company_name,
  page,
  referral_key,
  feature_name,
  path = getLocationPath()
}: TrackVirtualMeetingSchedulingButtonClickedParams): void {
  let element =
    'tax_results_national_pro_schedule_virtual_meeting' +
    (partnership_urn
      ? `_${partnership_urn.toLowerCase().replaceAll(/\s/g, '_')}`
      : ''
    ).replaceAll(/[^a-zA-Z0-9_:]/g, '');

  let label = element;

  getAnalytics()?.track(analytics_event_names.ELEMENT_CLICKED, {
    element,
    feature_name,
    label,
    page,
    path,
    referral_key,
    program: 'tax'
  });

  element =
    'tax_results_national_pro_schedule_virtual_meeting' +
    (company_name
      ? `_${company_name.toLowerCase().replaceAll(/\s/g, '_')}`
      : ''
    ).replaceAll(/\W/g, '');

  label = element;

  getAnalytics()?.track(analytics_event_names.ELEMENT_CLICKED, {
    element,
    feature_name,
    label,
    page,
    path,
    referral_key,
    program: 'tax'
  });
}

function trackExperimentViewed({
  experiment_id,
  variation_id,
  experiment_name,
  variation_name,
  experimentation_platform
}: TrackExperimentViewedParams): void {
  getAnalytics()?.track(analytics_event_names.EXPERIMENT_VIEWED, {
    experiment_id,
    variation_id,
    variation_name,
    experiment_name,
    experimentation_platform
  });
}

function identifyExperiment({
  userId,
  experiment_key,
  variation_key
}: IdentifyExperimentParams): void {
  getAnalytics()?.identify(userId, {
    [experiment_key]: variation_key
  });
}

interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

function getUtmParams(): UtmParams {
  const searchParams = new URLSearchParams(globalThis?.location?.search || '');
  const utmParams: UtmParams = {};
  const keys: (keyof UtmParams)[] = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content'
  ];
  for (const key of keys) {
    const value = searchParams.get(key);
    if (value) utmParams[key] = value;
  }
  return utmParams;
}

function getAnalytics() {
  if (isSyntheticMonitor()) return undefined;
  return globalThis?._APP_CONFIG?.segmentAnalytics as AnalyticsBrowser;
}

function getLocationPath(): string {
  return globalThis?.location?.pathname || '';
}

function getFullUrl() {
  return globalThis?.location?.href || '';
}

function getPageTitle() {
  return document.querySelector('title')?.textContent ?? undefined;
}

export {
  trackPageViewed,
  trackFormStarted,
  trackStepStarted,
  trackStepCompleted,
  trackStepErrorViewed,
  trackFormSubmitted,
  trackElementClicked,
  trackProfileAttributeViewed,
  trackVirtualMeetingSchedulingButtonClicked,
  trackExperimentViewed,
  trackMatchResultReturned,
  identifyExperiment,
  analytics_event_names,
  getFullUrl
};
